import * as createMatrix from "./createMatrix";



export const generateMutualMatchQueue = async (movers, sitters, eventData) => {
  const eventUsers = Object.values(eventData.eventUsers);
  const { matchType } = eventData
  const { consentMatrix, queueMatrix, matchedAttendees } = createMatrices(eventUsers, sitters, movers, matchType);



  return {
    queueMatrix,
    consentMatrix,
    movers: matchedAttendees.movers,
    sitters: matchedAttendees.sitters,
    removedMovers: [],
    removedSitters: [],
    at: new Date()
  };
};


export const regenerateMutualMatchQueue = async (event, currentRound = 0, eventData) => {
  const eventUsers = Object.values(eventData.eventUsers);
  const { matchType } = eventData
  // Reconstruct the old matrices from the event's queue
  const oldConsentMatrix = createMatrix.reconstructMatrix(event.queue.consentMatrix);
  const oldQueueMatrix = createMatrix.reconstructMatrix(event.queue.queueMatrix);

  const removeUnmatchedUsers = createMatrix.removeUnmatchedUsers(
    eventUsers,
    event.sitters,
    event.movers,
    matchType
  )

  const incomingSitters = removeUnmatchedUsers.sitters.filter(uid => !event.queue.sitters.includes(uid))
  const incomingMovers = removeUnmatchedUsers.movers.filter(uid => !event.queue.movers.includes(uid))

  const newSitters = [...event.queue.sitters, ...incomingSitters]
  const newMovers = [...event.queue.movers, ...incomingMovers]

  // Create new consent data based on updated eventUsers, sitters, and movers, excluding removed participants
  const removedMovers = event.queue.removedMovers || [];
  const removedSitters = event.queue.removedSitters || [];
  const newConsentData = createMatrix.createConsentMatrix(eventUsers, newSitters, newMovers, matchType);

  // Compute the difference between old and new consent matrices
  const newConsentMatrixDiff = createMatrix.matrixDiff(oldConsentMatrix, newConsentData.consentMatrix);

  if (newConsentMatrixDiff.length > 0) {

    // Generate new queue starting from currentRound using the new function
    // Pass participant data as a single object for processing within the function
    const newQueueMatrix = createMatrix.generateQueueRoundRobinFromRound(
      newConsentData.consentMatrix,
      currentRound,
      oldQueueMatrix,
      {
        removedMovers,
        removedSitters,
        movers: newMovers,
        sitters: newSitters
      }
    );

    // Flatten the queue matrix for further processing
    const queueMatrix = createMatrix.flattenMatrix(newQueueMatrix);

    // Flatten the new consent matrix
    const consentMatrix = createMatrix.flattenMatrix(newConsentData.consentMatrix);

    return {
      queueMatrix,
      consentMatrix,
      movers: newMovers,
      sitters: newSitters,
      removedMovers: event.queue.removedMovers || [],
      removedSitters: event.queue.removedSitters || [],
      at: new Date()
    };
  } else {
    throw new Error("No new changes found to regenerate queue");
  }
};


export const removeUserFromQueue = async (event, currentRound = 0, userId, userType) => {
  // Reconstruct the old matrices from the event's queue for reference
  const oldConsentMatrix = createMatrix.reconstructMatrix(event.queue.consentMatrix);
  const oldQueueMatrix = createMatrix.reconstructMatrix(event.queue.queueMatrix);

  // Create a new queue matrix as a deep copy of the old one to avoid modifying the original
  let newQueueMatrix = oldQueueMatrix.map(row => [...row]);

  // Retrieve the lists of sitters and movers from the event queue
  const sitters = event.queue.sitters;
  const movers = event.queue.movers;

  if (userType === 'sitter') {
    // If the user to remove is a sitter, clear all their future assignments starting from the current round
    const sitterIndex = sitters.indexOf(userId);
    if (sitterIndex !== -1) {
      for (let round = currentRound; round < newQueueMatrix.length; round++) {
        newQueueMatrix[round][sitterIndex] = null; // Remove assignment by setting to null
      }
    }
  } else if (userType === 'mover') {
    // If the user to remove is a mover, reassign their sitters to other available movers starting from the current round
    const moverIndex = movers.indexOf(userId);
    if (moverIndex !== -1) {
      for (let round = currentRound; round < newQueueMatrix.length; round++) {
        for (let sitterIndex = 0; sitterIndex < sitters.length; sitterIndex++) {
          // Check if the current sitter in this round is assigned to the mover being removed
          if (newQueueMatrix[round][sitterIndex] === moverIndex) {
            // Initialize a variable to store the index of a new mover to assign to this sitter
            let newMoverIndex = null;
            // Ensure the sitter index is within bounds and the consent matrix row is an array
            if (sitterIndex < oldConsentMatrix.length && Array.isArray(oldConsentMatrix[sitterIndex])) {
              // Iterate through all movers to find a suitable alternative
              for (let altMoverIndex = 0; altMoverIndex < movers.length; altMoverIndex++) {
                // Conditions to select an alternative mover:
                // 1. Must not be the mover being removed
                // 2. Must be within the bounds of the consent matrix for this sitter
                // 3. Sitter must have given consent to this alternative mover (value of 1 in consent matrix)
                // 4. Alternative mover must not already be assigned in this round
                if (altMoverIndex !== moverIndex &&
                  altMoverIndex < oldConsentMatrix[sitterIndex].length &&
                  oldConsentMatrix[sitterIndex][altMoverIndex] != null &&
                  !newQueueMatrix[round].includes(altMoverIndex)) {
                  newMoverIndex = altMoverIndex; // Assign the suitable alternative mover
                  break; // Exit loop once a suitable mover is found
                }
              }
            }
            // Update the queue matrix with the new mover index (or null if no suitable mover found)
            newQueueMatrix[round][sitterIndex] = newMoverIndex;
          }
        }
      }
    }
  }

  // Flatten the updated queue matrix for return
  const queueMatrix = createMatrix.flattenMatrix(newQueueMatrix);
  const consentMatrix = event.queue.consentMatrix;
  let removedMovers = event.queue.removedMovers || [];
  let removedSitters = event.queue.removedSitters || [];

  if (userType === 'mover' && !removedMovers.includes(userId)) {
    removedMovers = [...removedMovers, userId];
  } else if (userType === 'sitter' && !removedSitters.includes(userId)) {
    removedSitters = [...removedSitters, userId];
  }

  return {
    queueMatrix,
    consentMatrix,
    movers,
    sitters,
    removedMovers,
    removedSitters,
    at: new Date()
  };
};

//helper functions

const createMatrices = (eventUsers, sitters, movers, matchType) => {
  // Create the consent matrix based on consents, sitters, and movers
  const removeUnmatchedUsers = createMatrix.removeUnmatchedUsers(eventUsers, sitters, movers, matchType);
  const matchedAttendees = createMatrix.createConsentMatrix(eventUsers, removeUnmatchedUsers.sitters, removeUnmatchedUsers.movers, matchType);

  if (matchedAttendees.consentMatrix.length > 0) {
    // Flatten the consent matrix for easier processing
    const consentMatrix = createMatrix.flattenMatrix(matchedAttendees.consentMatrix);

    // Remove duplicates to generate queue data
    const queueData = createMatrix.generateQueueRoundRobin(matchedAttendees.consentMatrix);

    // Flatten the queue data to generate the queue matrix
    const queueMatrix = createMatrix.flattenMatrix(queueData);

    return { consentMatrix, queueMatrix, matchedAttendees };
  } else {
    throw new Error("No matches found to generate queue");
  }
};
